import { httpClient } from '@/services/http/httpClient';
import type {
  AddBrandColorRequest,
  BrandColorItem,
  BrandDnaAggregate,
  BrandDnaOptions,
  UpdateContentRequest,
  UpdateFineTuningRequest,
  UpdateGoalsRequest,
  UpdateIdentityRequest,
  UpdateVoiceRequest,
  SuggestedPillarsResponse,
} from '@/types';

/**
 * PUT /brand-dna/content rejects any topic name longer than this. It lives here
 * rather than in each page so the onboarding and Brand DNA surfaces that build
 * the payload can't drift apart from the endpoint's contract.
 */
export const MAX_TOPIC_NAME_LENGTH = 80;

/**
 * The same endpoint caps the topics array itself (`@ArrayMaxSize`), and an
 * over-long array 400s the whole Content tab, not just the topic being added.
 */
export const MAX_CONTENT_TOPICS = 10;

/** The same endpoint's `@MaxLength` on `topicId`. */
const MAX_TOPIC_ID_LENGTH = 40;

/**
 * Derive a server-valid topicId (`@IsString @MaxLength(40)`) for a topic that
 * has no server id yet. The index is always appended so two topics sharing a
 * long common prefix can't truncate to the same id — the BE upserts by
 * topicId, so a collision silently collapses them into one topic.
 */
export function slugifyTopicId(name: string, index: number): string {
  // Reserve room for the suffix rather than assuming a single digit, so the id
  // stays within the limit even if the topic cap is ever raised past 10.
  const suffix = `-${index}`;
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_TOPIC_ID_LENGTH - suffix.length)
    .replace(/-+$/g, '');
  return slug ? `${slug}${suffix}` : `topic-${index}`;
}

/**
 * Brand DNA data layer (LIL-311). The aggregate is the single source of truth
 * for the Brand DNA page (`Preferences.tsx`); every per-tab PUT also returns
 * the fresh aggregate so callers can re-hydrate without a second GET.
 */
export const brandDnaApi = {
  /** GET /api/brand-dna — full aggregate for the current user */
  async getAggregate() {
    return httpClient.get<BrandDnaAggregate>('/brand-dna');
  },

  /** GET /api/brand-dna/options — enum-backed dropdown options */
  async getOptions() {
    return httpClient.get<BrandDnaOptions>('/brand-dna/options');
  },

  /**
   * GET /api/brand-dna/suggested-pillars — suggested content pillars/topics
   * keyed by each of the user's saved niches (1-3, LIL-426) + default content
   * mix for the onboarding Content Topics step. Reads niches server-side; no params.
   */
  async getSuggestedPillars() {
    return httpClient.get<SuggestedPillarsResponse>('/brand-dna/suggested-pillars');
  },

  /** PUT /api/brand-dna/identity — returns the fresh aggregate */
  async updateIdentity(data: UpdateIdentityRequest) {
    return httpClient.put<BrandDnaAggregate>('/brand-dna/identity', data);
  },

  /** PUT /api/brand-dna/goals — returns the fresh aggregate */
  async updateGoals(data: UpdateGoalsRequest) {
    return httpClient.put<BrandDnaAggregate>('/brand-dna/goals', data);
  },

  /** PUT /api/brand-dna/content — returns the fresh aggregate */
  async updateContent(data: UpdateContentRequest) {
    return httpClient.put<BrandDnaAggregate>('/brand-dna/content', data);
  },

  /** PUT /api/brand-dna/voice — returns the fresh aggregate */
  async updateVoice(data: UpdateVoiceRequest) {
    return httpClient.put<BrandDnaAggregate>('/brand-dna/voice', data);
  },

  /** PUT /api/brand-dna/fine-tuning — returns the fresh aggregate */
  async updateFineTuning(data: UpdateFineTuningRequest) {
    return httpClient.put<BrandDnaAggregate>('/brand-dna/fine-tuning', data);
  },

  /** POST /api/brand-dna/brand-colors — add a colour */
  async addBrandColor(data: AddBrandColorRequest) {
    return httpClient.post<BrandColorItem>('/brand-dna/brand-colors', data);
  },

  /** DELETE /api/brand-dna/brand-colors/:id */
  async removeBrandColor(id: string) {
    return httpClient.delete<void>(`/brand-dna/brand-colors/${id}`);
  },
};
